// Food Response Parser Module
// Professional response parsing and validation for food scanning operations

// Temporary fix for TypeScript module resolution
export interface FoodItem {
  name: string;
  serving_size_g: number;
  serving_description: string;
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

export interface FoodItemWithId extends FoodItem {
  food_item_id: string;
}

export interface NutritionTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

export interface PredictFoodResponse {
  id: string;
  document_id: string;
  food_items: FoodItem[];
  ts: string;
  image_url: string;
  file_utility?: {
    document_id: string;
    original_file: string;
    optimized_file: string;
    thumbnail_file: string;
    file_hash: string;
    file_size: number;
    file_type: string;
  };
}

export interface AddFoodItemResponse {
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  message: string;
  added_item: {
    food_item_id: string;
    name: string;
    serving_size_g: number;
    serving_description: string;
  };
}

export interface EditFoodItemResponse {
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  message: string;
}

export interface DeleteFoodItemResponse {
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  message: string;
}

export interface SaveMealResponse {
  message: string;
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  file_utility: {
    document_id: string;
    original_file: string;
    optimized_file: string;
    thumbnail_file: string;
  };
}

export interface ParsedResponse<T = any> {
  success: boolean;
  data: T;
  confidence: number;
  source: "api_response" | "cache" | "fallback" | "error_recovery";
  warnings: string[];
  metadata?: Record<string, any>;
}

// Food prediction response parsing
export const parseFoodPredictionResponse = (
  rawResponse: any
): ParsedResponse<PredictFoodResponse> => {
  const warnings: string[] = [];
  let confidence = 1.0;

  try {
    // Validate basic structure
    if (!rawResponse || typeof rawResponse !== "object") {
      return {
        success: false,
        data: null as any,
        confidence: 0,
        source: "error_recovery",
        warnings: ["Invalid response structure"],
      };
    }

    // Extract food items
    const foodItems = rawResponse.food_items ?? [];
    if (!Array.isArray(foodItems)) {
      warnings.push("Food items is not an array");
      confidence -= 0.3;
    }

    // Validate and normalize food items
    const normalizedFoodItems: FoodItem[] = foodItems.map(
      (item: any, index: number) => {
        const normalizedItem: FoodItem = {
          name: item.name ?? `Unknown Food ${index + 1}`,
          serving_size_g: parseFloat(item.serving_size_g) ?? 100,
          serving_description: item.serving_description ?? "1 serving",
          carbohydrate_content_g: parseFloat(item.carbohydrate_content_g) || 0,
          dietary_fiber_g: parseFloat(item.dietary_fiber_g) || 0,
          net_carbs_g: parseFloat(item.net_carbs_g) || 0,
          sugar_g: parseFloat(item.sugar_g) || 0,
          protein_content_g: parseFloat(item.protein_content_g) || 0,
          fat_content_g: parseFloat(item.fat_content_g) || 0,
          trans_fat_g: parseFloat(item.trans_fat_g) || 0,
          saturated_fat_g: parseFloat(item.saturated_fat_g) || 0,
          calorie_content_kcal: parseFloat(item.calorie_content_kcal) || 0,
          GI_estimate: parseFloat(item.GI_estimate) || 0,
          GL_estimate: parseFloat(item.GL_estimate) || 0,
        };

        // Validate nutritional values
        if (normalizedItem.calorie_content_kcal <= 0) {
          warnings.push(`Food item ${index + 1}: No calorie information`);
          confidence -= 0.1;
        }

        if (normalizedItem.name.includes("Unknown")) {
          warnings.push(`Food item ${index + 1}: Name not recognized`);
          confidence -= 0.2;
        }

        return normalizedItem;
      }
    );

    // Build response object
    const parsedResponse: PredictFoodResponse = {
      id: rawResponse.id ?? generateFallbackId(),
      document_id:
        rawResponse.document_id ??
        rawResponse.file_utility?.document_id ??
        generateFallbackId(),
      food_items: normalizedFoodItems,
      ts: rawResponse.ts ?? new Date().toISOString(),
      image_url: rawResponse.image_url ?? "",
      file_utility: rawResponse.file_utility
        ? {
            document_id: rawResponse.file_utility.document_id ?? "",
            original_file: rawResponse.file_utility.original_file ?? "",
            optimized_file: rawResponse.file_utility.optimized_file ?? "",
            thumbnail_file: rawResponse.file_utility.thumbnail_file ?? "",
            file_hash: rawResponse.file_utility.file_hash ?? "",
            file_size: rawResponse.file_utility.file_size ?? 0,
            file_type: rawResponse.file_utility.file_type ?? "",
          }
        : undefined,
    };

    // Missing document_id warning
    if (!rawResponse.document_id && !rawResponse.file_utility?.document_id) {
      warnings.push("No document_id found in response");
      confidence -= 0.2;
    }

    // No food items detected
    if (normalizedFoodItems.length === 0) {
      warnings.push("No food items detected in image");
      confidence -= 0.5;
    }

    return {
      success: true,
      data: parsedResponse,
      confidence: Math.max(confidence, 0),
      source: "api_response",
      warnings,
      metadata: {
        foodItemCount: normalizedFoodItems.length,
        hasFileUtility: !!rawResponse.file_utility,
        originalKeys: Object.keys(rawResponse),
      },
    };
  } catch (error) {
    return {
      success: false,
      data: null as any,
      confidence: 0,
      source: "error_recovery",
      warnings: [
        `Parsing failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      ],
    };
  }
};

// Meal operation response parsing
export const parseMealOperationResponse = <
  T extends
    | AddFoodItemResponse
    | EditFoodItemResponse
    | DeleteFoodItemResponse
    | SaveMealResponse,
>(
  rawResponse: any,
  operationType: "add" | "edit" | "delete" | "save"
): ParsedResponse<T> => {
  const warnings: string[] = [];
  let confidence = 1.0;

  try {
    if (!rawResponse || typeof rawResponse !== "object") {
      return {
        success: false,
        data: null as any,
        confidence: 0,
        source: "error_recovery",
        warnings: ["Invalid meal operation response structure"],
      };
    }

    // Common validation for all meal operations
    const baseResponse = {
      meal_id: rawResponse.meal_id ?? generateFallbackId(),
      message: rawResponse.message ?? `${operationType} operation completed`,
      ts: rawResponse.ts ?? new Date().toISOString(),
      image_url: rawResponse.image_url ?? "",
      scan_type: rawResponse.scan_type ?? "manual",
    };

    // Validate meal_id
    if (!rawResponse.meal_id) {
      warnings.push("No meal_id in response");
      confidence -= 0.3;
    }

    // Parse food items if present
    let food_items: FoodItemWithId[] = [];
    if (rawResponse.food_items && Array.isArray(rawResponse.food_items)) {
      food_items = rawResponse.food_items.map((item: any, index: number) => ({
        food_item_id: item.food_item_id ?? `fallback_${index}`,
        name: item.name ?? `Food Item ${index + 1}`,
        serving_size_g: parseFloat(item.serving_size_g) || 100,
        serving_description: item.serving_description ?? "1 serving",
        carbohydrate_content_g: parseFloat(item.carbohydrate_content_g) || 0,
        dietary_fiber_g: parseFloat(item.dietary_fiber_g) || 0,
        net_carbs_g: parseFloat(item.net_carbs_g) || 0,
        protein_content_g: parseFloat(item.protein_content_g) || 0,
        fat_content_g: parseFloat(item.fat_content_g) || 0,
        trans_fat_g: parseFloat(item.trans_fat_g) || 0,
        saturated_fat_g: parseFloat(item.saturated_fat_g) || 0,
        calorie_content_kcal: parseFloat(item.calorie_content_kcal) || 0,
        GI_estimate: parseFloat(item.GI_estimate) || 0,
        GL_estimate: parseFloat(item.GL_estimate) || 0,
      }));
    }

    // Build operation-specific response
    const parsedResponse: any = {
      ...baseResponse,
      food_items,
    };

    // Add operation-specific fields
    switch (operationType) {
      case "add":
        if (rawResponse.added_item) {
          parsedResponse.added_item = {
            food_item_id:
              rawResponse.added_item.food_item_id ?? generateFallbackId(),
            name: rawResponse.added_item.name ?? "New Food Item",
            serving_size_g:
              parseFloat(rawResponse.added_item.serving_size_g) || 100,
            serving_description:
              rawResponse.added_item.serving_description ?? "1 serving",
          };
        } else {
          warnings.push("No added_item information in response");
          confidence -= 0.2;
        }
        break;

      case "save":
        if (rawResponse.file_utility) {
          parsedResponse.file_utility = {
            document_id: rawResponse.file_utility.document_id ?? "",
            original_file: rawResponse.file_utility.original_file ?? "",
            optimized_file: rawResponse.file_utility.optimized_file ?? "",
            thumbnail_file: rawResponse.file_utility.thumbnail_file ?? "",
          };
        }
        break;
    }

    return {
      success: true,
      data: parsedResponse as T,
      confidence: Math.max(confidence, 0),
      source: "api_response",
      warnings,
      metadata: {
        operationType,
        foodItemCount: food_items.length,
        hasFileUtility: !!rawResponse.file_utility,
      },
    };
  } catch (error) {
    return {
      success: false,
      data: null as any,
      confidence: 0,
      source: "error_recovery",
      warnings: [
        `Meal ${operationType} parsing failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      ],
    };
  }
};

// Upload response parsing
export const parseUploadResponse = (
  rawResponse: any
): ParsedResponse<{ document_id: string; file_info: any }> => {
  const warnings: string[] = [];
  const confidence = 1.0;

  try {
    if (!rawResponse) {
      return {
        success: false,
        data: null as any,
        confidence: 0,
        source: "error_recovery",
        warnings: ["No upload response received"],
      };
    }

    // Handle different upload response formats
    const possibleArray =
      rawResponse.results ?? rawResponse.data ?? rawResponse;
    const uploadItem = Array.isArray(possibleArray)
      ? possibleArray[0]
      : possibleArray;

    // Extract document ID from various possible fields
    const document_id =
      uploadItem?.id ??
      uploadItem?.file_utility_id ??
      uploadItem?.document_id ??
      rawResponse?.id ??
      rawResponse?.document_id;

    if (!document_id) {
      return {
        success: false,
        data: null as any,
        confidence: 0,
        source: "error_recovery",
        warnings: ["No document ID found in upload response"],
      };
    }

    return {
      success: true,
      data: {
        document_id,
        file_info: uploadItem ?? rawResponse,
      },
      confidence,
      source: "api_response",
      warnings,
      metadata: {
        responseFormat: Array.isArray(possibleArray) ? "array" : "object",
        originalKeys: Object.keys(rawResponse),
      },
    };
  } catch (error) {
    return {
      success: false,
      data: null as any,
      confidence: 0,
      source: "error_recovery",
      warnings: [
        `Upload parsing failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      ],
    };
  }
};

// Nutrition totals calculation
export const calculateNutritionTotals = (
  foodItems: FoodItem[]
): NutritionTotals => {
  return foodItems.reduce(
    (totals, item) => ({
      carbohydrate_content_g:
        totals.carbohydrate_content_g + (item.carbohydrate_content_g || 0),
      dietary_fiber_g: totals.dietary_fiber_g + (item.dietary_fiber_g || 0),
      net_carbs_g: totals.net_carbs_g + (item.net_carbs_g || 0),
      sugar_g: totals.sugar_g + 0, // Assuming sugar_g is not in FoodItem but in totals
      protein_content_g:
        totals.protein_content_g + (item.protein_content_g || 0),
      fat_content_g: totals.fat_content_g + (item.fat_content_g || 0),
      trans_fat_g: totals.trans_fat_g + (item.trans_fat_g || 0),
      saturated_fat_g: totals.saturated_fat_g + (item.saturated_fat_g || 0),
      calorie_content_kcal:
        totals.calorie_content_kcal + (item.calorie_content_kcal || 0),
      GI_estimate: Math.max(totals.GI_estimate, item.GI_estimate || 0), // Max GI
      GL_estimate: totals.GL_estimate + (item.GL_estimate || 0), // Sum of GL
    }),
    {
      carbohydrate_content_g: 0,
      dietary_fiber_g: 0,
      net_carbs_g: 0,
      sugar_g: 0,
      protein_content_g: 0,
      fat_content_g: 0,
      trans_fat_g: 0,
      saturated_fat_g: 0,
      calorie_content_kcal: 0,
      GI_estimate: 0,
      GL_estimate: 0,
    }
  );
};

// Validation helpers
export const validateFoodResponse = (response: any): boolean => {
  return response && typeof response === "object";
};

export const createFoodParsingErrorResponse = <T>(
  message: string = "Failed to parse food data"
): ParsedResponse<T> => {
  return {
    success: false,
    data: null as any,
    confidence: 0,
    source: "error_recovery",
    warnings: [message],
  };
};

// Utility functions
const generateFallbackId = (): string => {
  return `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
